import type Dexie from "dexie";

export interface OwnedDbConfig {
  /** localStorage key used to remember which user the DB belongs to. */
  ownerKey: string;
  /** Returns the currently active user id, or null if logged out. */
  getCurrentOwnerId: () => string | null;
  /**
   * What to do when `ensureOwnership()` runs with no active owner id.
   * - `true` (default): wipe the DB — privacy at rest, the original behavior.
   * - `false`: leave the DB intact (treat "no owner" as "can't verify yet").
   *   Use when cached data must survive logout AND the post-login window where
   *   the auth token lands (loggedIn=true) before the user id is loaded — a
   *   null-owner wipe in that window would destroy the data before the id
   *   arrives. Cross-user isolation is unaffected: a genuinely different owner
   *   is still wiped by the mismatch branch once its id is known.
   */
  clearWhenOwnerless?: boolean;
}

export interface OwnedDbHelpers {
  /** Lazily verifies the DB owner matches the current user; clears DB on mismatch. */
  ensureOwnership: () => Promise<void>;
  /** Forces the next ensureOwnership() call to re-check (use after logout/login). */
  invalidateOwnership: () => void;
  /**
   * Clears tables in the Dexie instance. A full clear (no `excludeTables`)
   * also forgets the owner key; a partial clear keeps the excluded tables AND
   * the owner key, so retained rows survive a same-user re-login.
   */
  clearAll: (excludeTables?: string[]) => Promise<void>;
}

/**
 * Wraps a Dexie instance with ownership-aware lifecycle helpers so the
 * cached data only ever belongs to a single logged-in user. Lifted from
 * user-app's original DbService so multiple apps can share the pattern.
 */
export function createOwnedDbHelpers(
  db: Dexie,
  config: OwnedDbConfig
): OwnedDbHelpers {
  let ownershipReady: Promise<void> | null = null;

  const clearAll = async (excludeTables: string[] = []): Promise<void> => {
    const tables = db.tables.filter((t) => !excludeTables.includes(t.name));
    if (tables.length > 0) {
      await db.transaction("rw", tables, async () => {
        await Promise.all(tables.map((t) => t.clear()));
      });
    }
    // A partial clear (caller deliberately kept some tables, e.g. logout that
    // preserves drafts/recents) must also preserve the owner key: the retained
    // rows stay attributed to that owner so a later same-user re-login is seen
    // as a match and ensureOwnership does NOT wipe them. Only a full clear
    // (no excludes) forgets the owner.
    if (excludeTables.length === 0) {
      localStorage.removeItem(config.ownerKey);
    }
  };

  const ensureOwnership = (): Promise<void> => {
    if (ownershipReady) {
      return ownershipReady;
    }
    const activeId = config.getCurrentOwnerId();
    // No active owner yet — ownership can't be verified. This notably happens
    // in the window right after login, where the auth token lands
    // (loggedIn=true) before the user id is loaded.
    if (!activeId) {
      if (config.clearWhenOwnerless ?? true) {
        // Default: wipe (privacy at rest), memoized like a real resolution.
        ownershipReady = clearAll();
        return ownershipReady;
      }
      // Survive-logout mode: don't wipe, and DON'T memoize — the owner id
      // arrives on a later call and must still be checked, otherwise a
      // different user could read the previous owner's data.
      return Promise.resolve();
    }
    ownershipReady = (async () => {
      const stored = localStorage.getItem(config.ownerKey);
      if (stored === activeId) {
        return;
      }
      await clearAll();
      localStorage.setItem(config.ownerKey, activeId);
    })();
    return ownershipReady;
  };

  const invalidateOwnership = (): void => {
    ownershipReady = null;
  };

  return { ensureOwnership, invalidateOwnership, clearAll };
}
